import { Alert, Box, Button, Typography, useTheme } from '@mui/material';
import { SyntheticEvent } from 'react';
import { useBoolean } from 'usehooks-ts';

import { ActionAccordion } from '@/components/Form/ActionList/ActionAccordion';
import { Icon } from '@/components/Icon/Icon';
import { FullPageLoaderOverlay } from '@/components/Layout/FullPageLoader';

import { useGetMinorDetailAndRequiredDocuments } from '@/hooks/minor/useGetminorDetailAndRequiredDocuments';
import { useUpdateOperationInfoAccuracy } from '@/hooks/minor/useUpdateInfoAccuracy';
import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { selectLoggedInUser } from '@/redux/auth/selectors';
import { selectOperationUser } from '@/redux/operation';
import { selectUserHolderJointMinor } from '@/redux/user-data';

import { DocumentStatus } from '@/types/document';

import { getAPIErrorMessage } from '@/utils/api';

type MinorWarrantyOfAccuracyAccordionProps = {
  disabled?: boolean;
};

export function MinorWarrantyOfAccuracyAccordion({
  disabled,
}: Readonly<MinorWarrantyOfAccuracyAccordionProps>) {
  const { t } = useTranslate();
  const user = useAppSelector(selectLoggedInUser);
  const holder = useAppSelector(selectUserHolderJointMinor);
  const operationUser = useAppSelector(selectOperationUser);
  const theme = useTheme();
  const { value: isExpanded, setValue: setExpanded } = useBoolean(false);
  const isConfirmed = !!operationUser?.infoAccuracyConfirmed;

  const { refetch } = useGetMinorDetailAndRequiredDocuments();

  const onSuccess = () => {
    refetch();
  };

  const { mutate, isPending, error, reset } = useUpdateOperationInfoAccuracy({
    onSuccess,
  });

  const handleAccordionExpand = (_event: SyntheticEvent, expanded: boolean) => {
    setExpanded(expanded);
  };

  const onConfirm = () => {
    if (!user?.operationId || !holder?.clientId) return;
    reset();
    mutate({
      operationId: user.operationId,
      clientId: holder.clientId,
      payload: true,
    });
  };

  return (
    <>
      {isPending && <FullPageLoaderOverlay />}
      <ActionAccordion
        title={t('moreInfo.form.warranty.minor.title')}
        status={isConfirmed ? DocumentStatus.Approved : undefined}
        expanded={isConfirmed ? false : isExpanded}
        onChange={handleAccordionExpand}
        disabled={disabled}
      >
        <Typography variant="body2" color={theme.palette.text.primary}>
          {t('moreInfo.form.warranty.body')}
        </Typography>
        {error && (
          <Box my={1.5}>
            <Alert
              icon={<Icon name="alert" />}
              color="error"
              variant="standard"
            >
              {getAPIErrorMessage(error)}
            </Alert>
          </Box>
        )}
        <Box textAlign="right" mt={0.5}>
          <Button onClick={onConfirm} disabled={disabled}>
            {t('moreInfo.form.warranty.button.confirm')}
          </Button>
        </Box>
      </ActionAccordion>
    </>
  );
}
